import clsx from 'clsx';
import type { ReactNode } from 'react';
import {
  BiBriefcase,
  BiCalendarEvent,
  BiDotsHorizontalRounded,
  BiHomeAlt,
  BiWrench,
} from 'react-icons/bi';
import { FiLock } from 'react-icons/fi';
import { NavLink, useLocation } from 'react-router-dom';

import type { Permission } from '@/permissions/permissions.catalog';
import { MAINTAIN_RIGHTS, MANAGE_RIGHTS } from '@/permissions/route-rights';
import { useAccessGate } from '@/permissions/useAccessGate';
import { useDrawerStore } from '@/store/drawer/drawer.store';

import styles from './BottomBar.module.scss';

type Tab = {
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * The rights the destination's route guard opens on, any-of. Omitted where the guard
   * names none — see `route-rights`, which is what both the guard and this bar read.
   */
  anyOf?: readonly Permission[];
};

const TABS: Tab[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <BiHomeAlt /> },
  { to: '/plan', label: 'Plan', icon: <BiCalendarEvent /> },
  { to: '/maintain', label: 'Maintain', icon: <BiWrench />, anyOf: MAINTAIN_RIGHTS },
  { to: '/manage', label: 'Manage', icon: <BiBriefcase />, anyOf: MANAGE_RIGHTS },
];

export const BottomBar = () => {
  const { pathname } = useLocation();
  const isDrawerOpen = useDrawerStore((s) => s.isOpen);
  const openDrawer = useDrawerStore((s) => s.open);
  const { anyVerdictFor, settled } = useAccessGate();

  /**
   * The bar's five slots are fixed, so a destination the viewer cannot reach is greyed out
   * in place rather than dropped — removing it would reshuffle the tabs under the thumb.
   *
   * `locked` stays clickable: that is the owner, and following it lands on the upgrade
   * placeholder the route guard renders, exactly as the drawer's padlocked rows do. Only
   * `hidden` — a right no upgrade returns — is dead. Before the verdicts settle everything
   * reads `hidden`, so nothing is disabled until then or the owner would watch half the bar
   * grey out and come back.
   */
  const isBlocked = (tab: Tab) => settled && !!tab.anyOf && anyVerdictFor(tab.anyOf) === 'hidden';

  return (
    <nav className={styles.bottomBar} aria-label="Primary">
      {TABS.map((tab) => {
        const blocked = isBlocked(tab);

        if (blocked) {
          return (
            <span
              key={tab.to}
              className={clsx(styles.tab, styles.tabDisabled)}
              aria-disabled="true"
              title={`You don’t have access to ${tab.label}.`}
            >
              {/*
               * The same padlock the drawer puts on a row it will not open, so a greyed-out
               * tab reads as withheld rather than as one that failed to load.
               */}
              <span className={styles.tabIconLocked} aria-hidden="true">
                {tab.icon}
                <FiLock className={styles.tabLockIcon} />
              </span>
              {tab.label}
            </span>
          );
        }

        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={clsx(styles.tab, pathname.startsWith(tab.to) && styles.tabActive)}
          >
            <span aria-hidden="true">{tab.icon}</span>
            {tab.label}
          </NavLink>
        );
      })}
      <button
        type="button"
        className={styles.tab}
        onClick={openDrawer}
        aria-label="More Navigation"
        aria-expanded={isDrawerOpen}
      >
        <span aria-hidden="true">
          <BiDotsHorizontalRounded />
        </span>
        More
      </button>
    </nav>
  );
};

export default BottomBar;
